import * as d3 from 'd3'
import { useRef } from 'react'
import { useD3, type D3Svg, type D3Dims } from './useD3.js'
import { CHART_PALETTE } from './chartTokens.js'
import { useChartTooltip, type TooltipVariant } from './useChartTooltip.js'
import { ChartLegend } from './ChartLegend.js'
import styles from './BarChart.module.css'

export interface BarDatum {
  label: string
  value: number
  color?: string
}

interface BarChartProps {
  data: BarDatum[]
  direction?: 'vertical' | 'horizontal'
  showTooltip?: boolean
  formatTooltip?: (datum: BarDatum) => string
  showLegend?: boolean
  tooltipVariant?: TooltipVariant
  className?: string
}

interface TooltipCtx {
  show: (x: number, y: number, text: string) => void
  hide: () => void
  format?: (datum: BarDatum) => string
}

const MARGIN = { top: 16, right: 16, bottom: 32, left: 48 }

function renderVertical(svg: D3Svg, dims: D3Dims, data: BarDatum[], tt?: TooltipCtx) {
  const w = dims.width - MARGIN.left - MARGIN.right
  const h = dims.height - MARGIN.top - MARGIN.bottom
  if (w <= 0 || h <= 0) return

  const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`)

  const x = d3.scaleBand()
    .domain(data.map((d) => d.label))
    .range([0, w])
    .padding(0.3)

  const y = d3.scaleLinear()
    .domain([0, d3.max(data, (d) => d.value) ?? 0])
    .nice()
    .range([h, 0])

  g.append('g')
    .attr('transform', `translate(0,${h})`)
    .call(d3.axisBottom(x).tickSize(0).tickPadding(8))
    .call((el) => el.select('.domain').attr('stroke', '#E8E4DF'))
    .selectAll('text')
    .attr('fill', '#9E9996')
    .attr('font-size', '11px')

  g.append('g')
    .call(d3.axisLeft(y).ticks(5).tickSize(-w).tickPadding(8))
    .call((el) => el.select('.domain').remove())
    .call((el) => el.selectAll('.tick line').attr('stroke', '#E8E4DF').attr('stroke-dasharray', '2,2'))
    .selectAll('text')
    .attr('fill', '#9E9996')
    .attr('font-size', '11px')

  const bars = g.selectAll('.bar')
    .data(data)
    .enter()
    .append('rect')
    .attr('x', (d) => x(d.label)!)
    .attr('y', (d) => y(d.value))
    .attr('width', x.bandwidth())
    .attr('height', (d) => h - y(d.value))
    .attr('fill', (d, i) => d.color ?? CHART_PALETTE[i % CHART_PALETTE.length])
    .attr('rx', 4)

  if (tt) {
    bars
      .style('cursor', 'pointer')
      .on('mouseover mousemove', (event, d) => {
        const text = tt.format?.(d) ?? `${d.label}: ${d.value}`
        const [mx, my] = d3.pointer(event, svg.node())
        tt.show(mx + 12, my - 10, text)
      })
      .on('mouseout', () => tt.hide())
  }
}

function renderHorizontal(svg: D3Svg, dims: D3Dims, data: BarDatum[], tt?: TooltipCtx) {
  const margin = { top: 16, right: 16, bottom: 16, left: 80 }
  const w = dims.width - margin.left - margin.right
  const h = dims.height - margin.top - margin.bottom
  if (w <= 0 || h <= 0) return

  const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

  const yScale = d3.scaleBand()
    .domain(data.map((d) => d.label))
    .range([0, h])
    .padding(0.3)

  const xScale = d3.scaleLinear()
    .domain([0, d3.max(data, (d) => d.value) ?? 0])
    .nice()
    .range([0, w])

  g.append('g')
    .call(d3.axisLeft(yScale).tickSize(0).tickPadding(8))
    .call((el) => el.select('.domain').remove())
    .selectAll('text')
    .attr('fill', '#9E9996')
    .attr('font-size', '11px')

  g.append('g')
    .attr('transform', `translate(0,${h})`)
    .call(d3.axisBottom(xScale).ticks(5).tickSize(-h).tickPadding(8))
    .call((el) => el.select('.domain').remove())
    .call((el) => el.selectAll('.tick line').attr('stroke', '#E8E4DF').attr('stroke-dasharray', '2,2'))
    .selectAll('text')
    .attr('fill', '#9E9996')
    .attr('font-size', '11px')

  const bars = g.selectAll('.bar')
    .data(data)
    .enter()
    .append('rect')
    .attr('x', 0)
    .attr('y', (d) => yScale(d.label)!)
    .attr('width', (d) => xScale(d.value))
    .attr('height', yScale.bandwidth())
    .attr('fill', (d, i) => d.color ?? CHART_PALETTE[i % CHART_PALETTE.length])
    .attr('rx', 4)

  if (tt) {
    bars
      .style('cursor', 'pointer')
      .on('mouseover mousemove', (event, d) => {
        const text = tt.format?.(d) ?? `${d.label}: ${d.value}`
        const [mx, my] = d3.pointer(event, svg.node())
        tt.show(mx + 12, my - 10, text)
      })
      .on('mouseout', () => tt.hide())
  }
}

export function BarChart({
  data,
  direction = 'vertical',
  showTooltip: enableTooltip = false,
  formatTooltip,
  showLegend = false,
  tooltipVariant,
  className,
}: BarChartProps) {
  const { tooltipRef, show, hide, tooltipClassName } = useChartTooltip(tooltipVariant)
  const formatRef = useRef(formatTooltip)
  formatRef.current = formatTooltip

  const ref = useD3(
    (svg, dims) => {
      const tt = enableTooltip ? { show, hide, format: formatRef.current } : undefined
      if (direction === 'horizontal') renderHorizontal(svg, dims, data, tt)
      else renderVertical(svg, dims, data, tt)
    },
    [data, direction, enableTooltip]
  )

  const legendItems = showLegend
    ? data.map((d, i) => ({
        label: d.label,
        color: d.color ?? CHART_PALETTE[i % CHART_PALETTE.length],
      }))
    : undefined

  return (
    <div className={`${styles.root}${className ? ` ${className}` : ''}`}>
      <svg ref={ref} className={styles.svg} />
      {enableTooltip && <div ref={tooltipRef} className={tooltipClassName} />}
      {legendItems && <ChartLegend items={legendItems} />}
    </div>
  )
}
